import { RELAY_SEED_NS, uuidv5 } from '../platform/uuid';
import type { Store } from './port';
import type { ExamLogTypeRow, LogReferenceType, ValueFormat } from './rows';

/**
 * Die `ExamLogType`-Stammdaten aus Relay. Sie gehören zum System und werden nicht im Betrieb
 * angelegt; die Kennung ist `uuidv5(NS, "ExamLogType/" + name)` und deshalb auf Gerät und
 * Server gleich.
 *
 * Wörtlich übernommen aus `RELAY/docs/schema/METIS-schema.md`, Abschnitt `### ExamLogType`.
 * Ändert sich dort etwas, ist es hier nachzuziehen — ein umbenannter Typ bekommt eine neue
 * Kennung, die alte Zeile wäre zu löschen.
 *
 * ⚠️ **An zwei Stellen läuft diese Liste dem Relay-Schema seit dem 2026-09-14 voraus**
 * (Festlegung des Nutzers, s. `docs/plan-medien-anzahl.md` und die Meldung
 * `docs/meldung-relay-handwriting-typ.md`):
 *
 * 1. `HANDWRITING_COMMENT` **gibt es dort noch nicht.** Die Handschrift lag bis dahin mit den
 *    Fotos und den Markierebenen im selben Topf `IMAGE_COMMENT` — und weil der Server je
 *    (Item, Typ) nur den jüngsten Eintrag auswertet, überschrieben die drei einander:
 *    schrieb die Handschrift „1", während drei Fotos vorlagen, sah der Server 1 statt 3.
 *    Solange die Zeile in der `ExamLogType`-Tabelle des Servers fehlt, weist er jede
 *    Handschriftzeile wegen des Fremdschlüssels ab.
 * 2. Die drei Medientypen tragen `NUMBER` statt `MEDIA_ID`. Ihr `value` ist keine
 *    Medienkennung mehr, sondern die **Anzahl** der Aufnahmen — der Server ordnet die Dateien
 *    selbst zu, indem er die übertragenen Anhänge scannt.
 *
 * ✅ **Beides steht seit dem 2026-09-14 auch auf dem Relay-Server** — von der Serversitzung
 * bestätigt, Kennung in ihrer Datenbank nachgesehen. ⚠️ Auf ihrem **Entwicklungsserver**;
 * beim Betreiber ist es damit noch nicht.
 *
 * ⚠️ **Diese Liste ist vollständig, obwohl Relay zwanzig Typen führt.** Der zwanzigste ist
 * `CANDIDATE_COMMENT` (`TEXT`), am 2026-09-12 ins Schema gekommen — die Bemerkung, die ein
 * **Prüfling** zu einer Frage schreibt, während er sie bearbeitet. Er gehört zur summativen
 * Prüfungs-App, nicht zu dieser: hier gibt es keinen Prüflings-Eingabeweg, wir schreiben ihn
 * nie, und `seedLogTypes` spielt genau diese Liste bei jedem Start ein.
 *
 * ⚠️ **Nicht mit `TEXT_COMMENT` verwechseln und erst recht nicht zusammenlegen.**
 * `CANDIDATE_COMMENT` ist der Prüfling, `TEXT_COMMENT` der Prüfer. Das Relay-Schema hat den
 * Satz ausdrücklich stehen.
 *
 * Am 2026-09-14 Name für Name gegen die laufende Relay-Datenbank verglichen: bis auf diesen
 * einen deckungsgleich. Wer hier eine Lücke vermutet, hat den gefunden — und soll ihn
 * **nicht** nachtragen.
 */

type Vorlage = [name: string, description: string, referenceType: LogReferenceType | null, valueFormat: ValueFormat];

const VORLAGEN = [
  ['CANDIDATE_LOGIN', 'Prüfling meldet sich am Gerät an', null, 'NONE'],
  ['CANDIDATE_LOGOUT', 'Prüfling meldet sich ab oder die Sitzung endet', null, 'NONE'],
  ['EXAM_START', 'Prüfling beginnt die Prüfung', 'EXAM', 'NONE'],
  ['EXAM_END', 'Prüfung wird beendet, abgegeben oder abgebrochen', 'EXAM', 'NONE'],
  ['PART_START', 'Prüfling betritt einen Prüfungsteil; Ziel in referenceIdentifier', 'EXAM_PART', 'NONE'],
  ['PART_END', 'Prüfling verlässt den Prüfungsteil', 'EXAM_PART', 'NONE'],
  ['CONTAINER_START', 'Prüfling betritt einen Item-Container, im OSCE die Station; Ziel in referenceIdentifier', 'EXAM_ITEM_CONTAINER', 'NONE'],
  ['CONTAINER_END', 'Prüfling verlässt den Item-Container bzw. die Station', 'EXAM_ITEM_CONTAINER', 'NONE'],
  ['ANSWER_GIVEN', 'Antwort auf eine Frage; Frage in referenceIdentifier', 'EXAM_QUESTION', 'JSON'],
  ['QUESTION_RATING', 'Punktvergabe durch einen Prüfer; Punkte in pointsNumerator', 'EXAM_ITEM', 'NONE'],
  ['TEXT_COMMENT', 'Freitext eines Prüfers; Text in value', 'EXAM_ITEM', 'TEXT'],
  ['CONNECTION_LOST', 'Gerät verliert die Verbindung zum Server', null, 'NONE'],
  ['AUDIO_COMMENT', 'Audio-Kommentar; Anzahl der Aufnahmen in value', 'EXAM_ITEM', 'NUMBER'],
  ['VIDEO_COMMENT', 'Video-Kommentar; Anzahl der Aufnahmen in value', 'EXAM_ITEM', 'NUMBER'],
  ['IMAGE_COMMENT', 'Foto-Kommentar samt Markierebene; Anzahl der Fotos in value', 'EXAM_ITEM', 'NUMBER'],
  ['HANDWRITING_COMMENT', 'Handschriftlicher Kommentar; Anzahl der Seiten in value', 'EXAM_ITEM', 'NUMBER'],
  ['SELECTED_WORDS', 'Indexes selektierter WÖRTER. Gespeichert als JSON-Array', 'EXAM_QUESTION', 'JSON_ARRAY'],
  ['ITEM_VISITED', 'Wenn ein Item angezeigt wird', 'EXAM_ITEM', 'NONE'],
  ['ANSWER_RATING', 'Punktvergabe durch einen Prüfer; Punkte in pointsNumerator', 'EXAM_ANSWER', 'NONE'],
] as const satisfies readonly Vorlage[];

export type LogTypeName = (typeof VORLAGEN)[number][0];

export const EXAM_LOG_TYPES: readonly ExamLogTypeRow[] = VORLAGEN.map(
  ([name, description, referenceType, valueFormat]) => ({
    id: uuidv5(RELAY_SEED_NS, 'ExamLogType/' + name),
    name,
    description,
    referenceType,
    valueFormat,
  }),
);

export function logTypeId(name: LogTypeName): string {
  return uuidv5(RELAY_SEED_NS, 'ExamLogType/' + name);
}

/** Beim Öffnen der Ablage einmal einspielen; wiederholbar, weil `put` anhand der `id` ersetzt. */
export async function seedLogTypes(store: Store): Promise<void> {
  await store.put('ExamLogType', [...EXAM_LOG_TYPES]);
}
